import React, { useMemo } from 'react';
import {
  Bar, BarChart, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, LabelList,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Loader2 } from 'lucide-react';
import { formatCompact } from '../../lib/chartFormatters';
import { ForecastBarShape, ForecastCaveat } from '../../lib/forecastChartUtils';
import { cn } from '../../lib/utils';
import { MoMGrowthRow } from './MoMGrowthRow';

/**
 * Shared BR OS Overview chart matching the PP Monthly Gross Revenue
 * treatment: full calendar-year x-axis, dashed forecast bars, above-bar
 * value labels, PP-style YTD footer with coloured series dots.
 *
 * Both Revenue and Costs Overview render through this component so
 * they cannot diverge in structure.
 *
 * Props destructured in body (babel-metadata-plugin workaround).
 */
function BROSOverviewChart(props) {
  const {
    data,
    series,
    title,
    subtitle,
    caption,
    dc,
    loading,
    emptyMessage,
    tooltipContent,
    footerLabel,
    footerItems,
    hasForecast,
    onBarClick,
    notes,
    growthDataKey,
    growthLabel,
  } = props;

  const dataPresent = data && data.length > 0;
  const hasValues = dataPresent && data.some(function (d) {
    return series.some(function (s) {
      return !s.hidden && (d[s.dataKey] ?? 0) > 0;
    });
  });

  // Compute MoM growth from the specified data key
  var growthRowData = useMemo(function () {
    if (!growthDataKey || !data || data.length === 0) return null;
    var result = [];
    var prevVal = null;
    for (var i = 0; i < data.length; i++) {
      var d = data[i];
      var val = d[growthDataKey];
      var growth = null;
      if (!d._isForecast && prevVal != null && prevVal > 0 && val != null) {
        growth = ((val - prevVal) / prevVal) * 100;
      }
      result.push({ monthKey: d.monthKey ?? i, MoMGrowth: growth });
      if (!d._isForecast) prevVal = val;
    }
    return result;
  }, [data, growthDataKey]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
        {subtitle}
        {caption && (
          <p className="text-xs text-muted-foreground mt-0.5" data-testid="chart-caption">
            {caption}
          </p>
        )}
        <ForecastCaveat show={!!hasForecast} />
      </CardHeader>
      <CardContent>
        {loading && !hasValues && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}
        {hasValues && (
          <>
            <ResponsiveContainer width="100%" height={340}>
              <BarChart
                data={data}
                margin={{ top: 30, right: 40, left: 0, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="hsl(var(--border))"
                />
                <XAxis
                  dataKey="month"
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={12}
                  tickLine={false}
                />
                <YAxis
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={12}
                  tickFormatter={function (v) { return formatCompact(v, dc); }}
                />
                {tooltipContent && <Tooltip content={tooltipContent} />}
                <Legend />
                {series
                  .filter(function (s) { return !s.hidden; })
                  .map(function (s) {
                    return (
                      <Bar
                        key={s.dataKey}
                        dataKey={s.dataKey}
                        name={s.name ?? s.dataKey}
                        fill={s.color}
                        stackId={s.stackId}
                        fillOpacity={s.fillOpacity}
                        stroke={s.stroke}
                        strokeDasharray={s.strokeDasharray}
                        strokeWidth={s.strokeWidth}
                        shape={
                          s.useShape !== false
                            ? <ForecastBarShape data={data} />
                            : undefined
                        }
                        cursor={onBarClick ? 'pointer' : undefined}
                        onClick={
                          onBarClick
                            ? function (barData) { onBarClick(s.dataKey, barData); }
                            : undefined
                        }
                      >
                        <LabelList
                          dataKey={s.dataKey}
                          position="top"
                          content={
                            s.labelContent ??
                            function ({ x, y, width, value }) {
                              if (!value || value < 100) return null;
                              return (
                                <text
                                  x={x + width / 2}
                                  y={y - 6}
                                  textAnchor="middle"
                                  fontSize={10}
                                  fill={s.color}
                                >
                                  {formatCompact(value, dc)}
                                </text>
                              );
                            }
                          }
                        />
                      </Bar>
                    );
                  })}
              </BarChart>
            </ResponsiveContainer>
            {notes}
            {/* PP-style YTD footer with coloured series dots */}
            {footerLabel && footerItems && footerItems.length > 0 && (
              <div
                className="mt-2 text-sm text-muted-foreground flex items-center gap-4 flex-wrap"
                data-testid="overview-chart-footer"
              >
                <span>{footerLabel}:</span>
                {footerItems.map(function (item) {
                  return (
                    <span
                      key={item.label}
                      className="inline-flex items-center gap-1"
                    >
                      <span
                        className="inline-block w-2.5 h-2.5 rounded-full"
                        style={{ backgroundColor: item.color }}
                      />
                      <span>{item.label}</span>
                      <span
                        className={cn(
                          'font-mono font-semibold',
                          item.className ?? 'text-foreground'
                        )}
                      >
                        {item.formatted ?? formatCompact(item.value, dc)}
                      </span>
                    </span>
                  );
                })}
              </div>
            )}
            {/* MoM growth row at the bottom */}
            <MoMGrowthRow growthRowData={growthRowData} growthLabel={growthLabel} />
          </>
        )}
        {!loading && !hasValues && (
          <p className="text-sm text-muted-foreground py-8 text-center">
            {emptyMessage ?? 'No data for this period.'}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

export { BROSOverviewChart };
